import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { demoFocus, demoRecommendations } from '../../data/landingBVisual';
import { demoIdentity } from '../../data/landingVisual';

/**
 * Personalisation, shown as the product actually renders it — a focused day and
 * a set of suggestions — sitting beside the control the student has over it.
 *
 * The privacy panel is deliberately part of the same section rather than a
 * separate reassurance later on: the claim and its limit belong together.
 */
const PROMISES = ['You choose what to share', 'We protect your data', 'You can change your preferences anytime'];
export function PersonalizationB() {
  return <section className="mx-auto max-w-[88rem] px-4 pb-14 sm:px-8" data-id="element-2660" data-mp="a2hz07ewvx88">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)_minmax(0,0.85fr)]" data-id="element-2661" data-mp="1pk1v2pgc8dxd">
        {/* -------------------------------------------------- today's focus */}
        <div className="cz-preview overflow-hidden rounded-card border border-line bg-surface" data-id="element-2662" data-mp="1mep2rmzyhvp5">
          <div className="border-b border-line px-4 py-3" data-id="element-2663" data-mp="1rd0fq6ljsmbl">
            <p className="text-[15px] font-bold text-ink" data-id="element-2664" data-mp="1rfd3x1hwfsx3">
              Good morning, {demoIdentity.name.split(' ')[0]} 👋
            </p>
            <p className="mt-0.5 text-[11px] text-ink-muted" data-id="element-2665" data-mp="20egne6kzaac4">Here&rsquo;s your day at a glance.</p>
          </div>
          <ul className="divide-y divide-line" data-id="element-2666" data-mp="13oh69nqri3hx">
            {demoFocus.map(item => <li key={item.time} className="flex items-start gap-3 px-4 py-3" data-id="element-2667" data-mp="17orllr0g269g">
                <span className={`cz-num w-11 shrink-0 rounded-md px-1.5 py-0.5 text-center text-[11px] font-semibold ${item.now ? 'bg-brand-700 text-white' : 'bg-canvas text-ink-muted'}`} data-id="element-2668" data-mp="1cochtto8c0ih">
                  {item.time}
                </span>
                <span className="min-w-0" data-id="element-2669" data-mp="oy9yc1yds2vh">
                  <span className="block truncate text-[13px] font-semibold text-ink" data-id="element-2670" data-mp="owyszxpaen29">
                    {item.title}
                  </span>
                  <span className="block truncate text-[11px] text-ink-muted" data-id="element-2671" data-mp="1naumnuoz8sei">{item.note}</span>
                </span>
              </li>)}
          </ul>
        </div>

        {/* ------------------------------------------------- recommendations */}
        <div className="rounded-card border border-line bg-surface p-4" data-id="element-2672" data-mp="1x2ihm5kqsrkq">
          <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-2673" data-mp="19bmjkqpp1051">
            <h2 className="text-h4 font-semibold tracking-tight text-ink" data-id="element-2674" data-mp="14cibk3ei98ga">Recommended for you</h2>
            <p className="text-caption text-ink-subtle" data-id="element-2675" data-mp="1ibrngqvohgeg">
              Because of your programme and your campus
            </p>
          </div>
          <ul className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-2676" data-mp="1dej62sictge9">
            {demoRecommendations.map(item => <li key={item.title} className="overflow-hidden rounded-xl border border-line" data-id="element-2677" data-mp="2orllvlcmq1xd">
                <img src={item.image} alt="" loading="lazy" decoding="async" className="h-24 w-full object-cover" data-id="element-2678" data-mp="2x8lwnplepl37" />
                <div className="p-2.5" data-id="element-2679" data-mp="2naxz6rwf1zhc">
                  <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2680" data-mp="2nbzghe40zi7i">
                    {item.kind}
                  </p>
                  <p className="mt-1 truncate text-body-sm font-semibold text-ink" data-id="element-2681" data-mp="3fyk5i7x13i7w">{item.title}</p>
                  <p className="cz-num mt-0.5 text-body-sm font-semibold text-brand-800" data-id="element-2682" data-mp="3kyoktum7a6wj">
                    {item.price}
                  </p>
                </div>
              </li>)}
          </ul>
          <p className="mt-3 text-caption text-ink-subtle" data-id="element-2683" data-mp="3b7llax8x5zzd">
            Demonstration content. Real suggestions come from your own courses, campus and saved
            items — and every one of them says why it appeared.
          </p>
        </div>

        {/* ------------------------------------------------------- privacy */}
        <div className="relative overflow-hidden rounded-card bg-brand-900 p-6" data-id="element-2684" data-mp="2f4eqcga5qqta">
          <ShieldCheckIcon aria-hidden className="pointer-events-none absolute -bottom-6 -right-6 h-40 w-40 text-white/10" data-id="element-2685" data-mp="2b0grm6oqvi0r" />
          <h2 className="relative text-h3 font-bold leading-tight tracking-tight text-white" data-id="element-2686" data-mp="213c2pz3il8g9">
            Personalized.
            <br data-id="element-2687" data-mp="2yr40ovaecdow" />
            Not intrusive.
          </h2>
          <p className="relative mt-4 text-body-sm leading-relaxed text-white/70" data-id="element-2688" data-mp="2ysnjasjiz0tu">
            You stay in control of your information and how it&rsquo;s used.
          </p>
          <ul className="relative mt-5 space-y-2.5" data-id="element-2689" data-mp="2ozggnfg65yxg">
            {PROMISES.map(promise => <li key={promise} className="flex items-start gap-2.5 text-body-sm text-white/85" data-id="element-2690" data-mp="2k1ewssax5vkx">
                <CheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand-200" data-id="element-2691" data-mp="3hiwtaoljzww3" />
                {promise}
              </li>)}
          </ul>
          <Link to="/legal/privacy" className="group relative mt-6 inline-flex min-h-touch items-center gap-2 text-body-sm font-semibold text-white" data-id="element-2692" data-mp="3k2a3cvwb4sbx">
            Your privacy, our promise
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2693" data-mp="3tz4yr5xaaclz" />
          </Link>
        </div>
      </div>
    </section>;
}